import {
    selectWorkspaceMembers,
    selectWorkspaceMembersById,
} from "features/entity-caching/members/UserManagementSlice"
import { getSharingMembers } from "features/overview/sharing-location/sharingMembers"
import { selectMembersSort } from "features/overview/slice"
import { useOrgUserLocations } from "features/user-locations/org-user-location-store"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { useCallback } from "react"
import { z } from "zod"
import {
    SharingMemberFullPanelResult,
    SharingMembersFullPanelEmpty,
    SharingMembersFullPanelHeader,
    WorkspaceMemberSearchResult,
} from "../../searchResults/WorkspaceMemberSearchResult"
import { defineActivatablePlugin } from "../types"
import { matchesMemberQuery } from "./matchesMemberQuery"

const workspaceMemberSearchResultDataSchema = z.object({
    userId: z.string(),
})

export const membersPlugin = defineActivatablePlugin({
    name: "members",
    displayName: "Members",
    fullPanelSearchPlaceholder: "Search for people sharing location",
    useSearch: () => {
        const members = useAppSelector(selectWorkspaceMembers)
        const sharingLocations = useOrgUserLocations()
        const sort = useAppSelector(selectMembersSort)
        return useCallback(
            async (query, ctx) => {
                if (!ctx.orgId) return []

                if (ctx.active) {
                    return getSharingMembers({
                        state: store.getState(),
                        locations: sharingLocations,
                        query,
                        sort,
                    }).map(({ userId }) => ({ userId }))
                }

                const q = query.toLowerCase()
                return members
                    .filter((member) => matchesMemberQuery(member, q))
                    .sort(
                        (a, b) =>
                            Number(sharingLocations.has(b.id)) - Number(sharingLocations.has(a.id))
                    )
                    .map((member) => ({ userId: member.userId }))
            },
            [members, sharingLocations, sort]
        )
    },
    dataSchema: workspaceMemberSearchResultDataSchema,
    Component: WorkspaceMemberSearchResult,
    FullPanelHeaderComponent: SharingMembersFullPanelHeader,
    FullPanelResultComponent: SharingMemberFullPanelResult,
    FullPanelEmptyComponent: SharingMembersFullPanelEmpty,
    getKey: (data) => data.userId,
    getSearchableText: (data) => {
        const member = selectWorkspaceMembersById(store.getState())[data.userId]
        return member ? `${member.fullName} ${member.email}` : ""
    },
})
